import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { formatDate } from '@angular/common';
import { addMinutes, addSeconds, differenceInMinutes, endOfDay, endOfHour, format, isSameDay, isSameHour, startOfDay, startOfHour, startOfMinute, subMinutes } from 'date-fns';
import { CameraTimeline } from 'app/cameras/models';

export interface SegmentRecord {
    type: 'gap' | 'future' | 'recording';
    start_time: string;
    end_time: string;
    duration: number;
};

@Component({
    selector: 'app-video-timeline',
    templateUrl: './video-timeline.component.html',
    styleUrls: ['./video-timeline.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class VideoTimelineComponent implements OnInit, OnChanges, OnDestroy {
    startDate = startOfDay(new Date());
    endDate = endOfDay(new Date());
    showLabel = false;
    dateLabel = '';
    labelPosition: any = {};
    private privateSegmentsRecord: Array<CameraTimeline> = [];
    fullTimeline: Array<any> = [];
    currentTimePosition = '0%';
    intervalUnsubscribe: NodeJS.Timeout | null = null;
    intervalGenerateTimelineUnsubscribe: NodeJS.Timeout | null = null;
    isClicked = false; // Flag to track if the user clicked
    clickedTime = false;
    currentTimeMarker = new Date();

    @Input({ required: true }) segmentsRecord: Array<CameraTimeline> = [];
    @Input({ required: true }) totalMinutes = 1440;
    @Input() isLive: boolean;
    @Input() isPaused: boolean;
    @Input() currentTimelineDate: Date;
    @Input() playbackRate = 1;
    @Output() timeClicked = new EventEmitter<string>();
    @Output() timeChanged = new EventEmitter<string>();

    startIntervalTimemarker(name?: string) {
        if (this.intervalUnsubscribe) {
            clearInterval(this.intervalUnsubscribe);
        }
        this.intervalUnsubscribe = setInterval(() => {
            if (this.currentTimeMarker) {
                this.currentTimeMarker = addSeconds(this.currentTimeMarker, this.playbackRate);
            }
            this.updateCurrentTimeMarker(this.currentTimeMarker);
        }, 1000);
    }

    stopIntervalTimemarker() {
        if (this.intervalUnsubscribe) {
            clearInterval(this.intervalUnsubscribe);
            this.intervalUnsubscribe = null;
        }
    }

    ngOnInit(): void {
        this.updateCurrentTimeMarker(new Date());
        this.intervalGenerateTimelineUnsubscribe = setInterval(() => {
            this.generateTimelineWithGaps();
        }, 300000);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['playbackRate'] && !changes['playbackRate'].firstChange) {
            this.restartMarkerInterval();
        }

        if (changes['isLive']) {
            this.isLive = changes['isLive'].currentValue;
        }

        if (changes['currentTimelineDate']) {
            const currentTimelineDate = changes['currentTimelineDate'].currentValue;
            if (!this.isLive) {
                this.currentTimeMarker = currentTimelineDate;
                this.startIntervalTimemarker('currentTimelineDate');
            }
            else if (this.isCurrentDate(currentTimelineDate)) {
                if (this.intervalUnsubscribe) {
                    clearInterval(this.intervalUnsubscribe);
                } else {
                    this.startIntervalTimemarker('isCurrentDate');
                }
            }
            else {
                this.currentTimePosition = '-1%';
                clearInterval(this.intervalUnsubscribe);
                this.intervalUnsubscribe = null;
            }
            this.currentTimeMarker = currentTimelineDate;
        }

        if (changes['totalMinutes']) {
            this.totalMinutes = changes['totalMinutes'].currentValue;
        }

        if (changes['segmentsRecord']) {
            const newSegments = changes['segmentsRecord'].currentValue;
            if (!newSegments) {
                this.privateSegmentsRecord = [];
                this.fullTimeline = [];
                return;
            }
            if (!this.objectsEqual(newSegments, this.privateSegmentsRecord)) {
                this.privateSegmentsRecord = newSegments || [];
                this.fullTimeline = this.generateTimelineWithGaps();
            } else {
                console.log('Os dados são iguais. Nenhuma atualização necessária.');
            }
        }

        if (changes['isPaused']) {
            if (changes['isPaused'].currentValue === true) {
                clearInterval(this.intervalUnsubscribe);
            }
        }
    }

    ngOnDestroy(): void {
        if (this.intervalUnsubscribe) {
            clearInterval(this.intervalUnsubscribe);
        }
        if (this.intervalGenerateTimelineUnsubscribe) {
            clearInterval(this.intervalGenerateTimelineUnsubscribe);
        }
    }

    onMouseMove(event: MouseEvent): void {
        const container = event.currentTarget as HTMLElement;
        const containerWidth = container.offsetWidth;
        const mouseX = event.offsetX;

        const target = event.target as HTMLElement;

        if (target.classList.contains('time-marker')) {
            return;
        }

        const timeElapsed = (mouseX / containerWidth) * (this.endDate.getTime() - this.startDate.getTime());
        const currentDate = new Date(this.startDate.getTime() + timeElapsed);

        this.dateLabel = formatDate(currentDate, 'yyyy-MM-dd HH:mm:ss', 'en-US');
        this.labelPosition = { left: `${mouseX}px` };
        this.showLabel = true;
    }

    onMouseClick(event: MouseEvent): void {
        const container = event.currentTarget as HTMLElement;
        const containerWidth = container.offsetWidth;
        const mouseX = event.offsetX;
        const timeElapsed = (mouseX / containerWidth) * (this.endDate.getTime() - this.startDate.getTime());
        const currentDate = new Date(this.startDate.getTime() + timeElapsed);
        this.currentTimeMarker = currentDate;
        this.clickedTime = true;
        this.timeClicked.emit(format(currentDate, 'yyyy-MM-dd HH:mm:ss'));
        clearInterval(this.intervalUnsubscribe);
        this.updateCurrentTimeMarker(currentDate);
    }

    onMouseLeave(): void {
        this.showLabel = false;
    }

    getMinutesFromStartOfInterval(dateTime: string): number {
        const date = new Date(dateTime);
        if (!this.startDate) {return 0; }

        const diffMs = date.getTime() - this.startDate.getTime();
        const diffMinutes = diffMs / (1000 * 60);
        return Math.max(0, Math.floor(diffMinutes)); // nunca retorna negativo
    }

    getSegmentWidth(duration: number): number {
        if (duration <= 0 || this.totalMinutes <= 0) {return 0; }

        const raw = (duration / this.totalMinutes) * 100;
        const clamped = Math.min(raw, 100); // nunca passa de 100%
        return Math.round(clamped * 100) / 100; // arredonda pra 2 casas
    }

    getSegmentLeft(startTime: number): number {
        const percentage = (startTime / this.totalMinutes) * 100;
        return Math.ceil(percentage * 100) / 100;
    }

    getDatesByInterval(minutes: number, now: Date) {
        if (minutes === 10) {
            const adjustedStart = subMinutes(startOfMinute(now), now.getMinutes() % 10);
            return {
                startOfInterval: adjustedStart,
                endOfInterval: addMinutes(adjustedStart, 10),
            };
        } else if (minutes === 60) {
            return {
                startOfInterval: startOfHour(now),
                endOfInterval: endOfHour(now)
            };
        }
        return {
            startOfInterval: startOfDay(now),
            endOfInterval: endOfDay(now),
        };
    }

    generateTimelineWithGaps(): Array<any> {
        const timeline: SegmentRecord[] = [];

        const sortedSegments = this.privateSegmentsRecord.sort((a, b) => {
            return new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
        });

        const referenceDate = this.currentTimeMarker || new Date();
        const { startOfInterval, endOfInterval } = this.getDatesByInterval(this.totalMinutes, referenceDate);
        const currentMinutes = differenceInMinutes(referenceDate, startOfInterval);
        const endOfDayMinutes = differenceInMinutes(endOfInterval, startOfInterval);

        this.startDate = startOfInterval;
        this.endDate = endOfInterval;

        let previousEnd = 0;

        sortedSegments.forEach((segment) => {
            const segmentStart = this.getMinutesFromStartOfInterval(segment.start_time);
            const segmentEnd = this.getMinutesFromStartOfInterval(segment.end_time);

            const limitedStart = Math.max(segmentStart, 0);
            const limitedEnd = Math.min(segmentEnd, endOfDayMinutes);

            // Se houver espaço entre o fim do anterior e o início deste
            if (limitedStart > previousEnd) {
                const gapStart = new Date(startOfInterval.getTime() + previousEnd * 60000);
                const gapEnd = new Date(startOfInterval.getTime() + limitedStart * 60000);

                if (this.isCurrentDate(referenceDate)) {
                    if (currentMinutes > previousEnd && currentMinutes <= limitedStart) {
                        timeline.push({
                            type: 'gap',
                            start_time: format(gapStart, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                            end_time: format(referenceDate, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                            duration: currentMinutes - previousEnd,
                        });

                        timeline.push({
                            type: 'future',
                            start_time: format(referenceDate, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                            end_time: format(gapEnd, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                            duration: limitedStart - currentMinutes,
                        });
                    } else if (currentMinutes <= previousEnd) {
                        // tudo é future
                        timeline.push({
                            type: 'future',
                            start_time: format(gapStart, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                            end_time: format(gapEnd, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                            duration: limitedStart - previousEnd,
                        });
                    } else {
                        // tudo é gap
                        timeline.push({
                            type: 'gap',
                            start_time: format(gapStart, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                            end_time: format(gapEnd, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                            duration: limitedStart - previousEnd,
                        });
                    }
                } else {
                    timeline.push({
                        type: 'gap',
                        start_time: format(gapStart, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                        end_time: format(gapEnd, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                        duration: limitedStart - previousEnd,
                    });
                }
            }

            // Segmento de gravação (recording)
            if (limitedEnd > limitedStart) {
                timeline.push({
                    type: 'recording',
                    start_time: format(new Date(startOfInterval.getTime() + limitedStart * 60000), 'yyyy-MM-dd\'T\'HH:mm:ss'),
                    end_time: format(new Date(startOfInterval.getTime() + limitedEnd * 60000), 'yyyy-MM-dd\'T\'HH:mm:ss'),
                    duration: limitedEnd - limitedStart,
                });
            }

            previousEnd = Math.max(previousEnd, limitedEnd);
        });

        // Verifica se após o último segmento ainda sobra tempo
        if (previousEnd < endOfDayMinutes) {
            const gapStart = new Date(startOfInterval.getTime() + previousEnd * 60000);

            if (this.isCurrentDate(referenceDate)) {
                if (currentMinutes > previousEnd) {
                    timeline.push({
                        type: 'gap',
                        start_time: format(gapStart, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                        end_time: format(referenceDate, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                        duration: currentMinutes - previousEnd,
                    });
                }

                if (currentMinutes < endOfDayMinutes) {
                    timeline.push({
                        type: 'future',
                        start_time: format(referenceDate, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                        end_time: format(endOfInterval, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                        duration: endOfDayMinutes - currentMinutes,
                    });
                }
            } else {
                timeline.push({
                    type: 'gap',
                    start_time: format(gapStart, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                    end_time: format(endOfInterval, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                    duration: endOfDayMinutes - previousEnd,
                });
            }
        }

        this.updateCurrentTimeMarker(referenceDate);

        return timeline;
    }

    isCurrentDate(date: Date): boolean {
        const now = new Date();

        if (this.totalMinutes === 1440) {
            return isSameDay(date, now);
        } else if (this.totalMinutes === 60) {
            return isSameHour(date, now);
        }

        return false;
    }

    getSegmentColor(type: string): string {
        switch (type) {
            case 'recording':
                return '#6CACFF';
            case 'gap':
                return '#002D59';
            case 'future':
                return 'rgba(243, 249, 255, 1)';
            default:
                return '#002D59';
        }
    }

    updateCurrentTimeMarker(currentDate: Date): void {
        const totalDuration = this.endDate.getTime() - this.startDate.getTime();
        let targetDate = currentDate;

        // Se o currentDate estiver fora do intervalo, tenta pegar a primeira gravação disponível
        if (currentDate.getTime() < this.startDate.getTime() || currentDate.getTime() > this.endDate.getTime()) {
            const firstRecording = this.fullTimeline.find(t => t.type === 'recording');
            if (firstRecording) {
                targetDate = new Date(firstRecording.start_time);
            } else {
                // Se nem gravação tiver, esconde o marker
                this.currentTimePosition = '-1%';
                return;
            }
        }

        const elapsed = targetDate.getTime() - this.startDate.getTime();
        const percentage = (elapsed / totalDuration) * 100;
        this.currentTimePosition = `${Math.max(0, Math.min(percentage, 100))}%`;

        this.timeChanged.emit(format(targetDate, 'yyyy-MM-dd HH:mm:ss'));
    }

    clearIntervalTimemarker(): void {
        if (this.intervalUnsubscribe) {
            clearInterval(this.intervalUnsubscribe);
            this.intervalUnsubscribe = null;
        }
    }

    restartMarkerInterval(): void {
        this.clearIntervalTimemarker();
        this.startIntervalTimemarker();
    }

    showCurrentTimeLabel(): void {
        const referenceDate = this.currentTimeMarker || new Date();
        this.dateLabel = formatDate(referenceDate, 'yyyy-MM-dd HH:mm:ss', 'pt-BR');
        this.labelPosition = { left: this.currentTimePosition };
    }

    trackByFn(index: number, item: any) {
        return item.start_time;
    }

    objectsEqual = (o1, o2) => {
        if (o2 === null && o1 !== null) {return false; }
        return o1 !== null && typeof o1 === 'object' && Object.keys(o1).length > 0 ?
            Object.keys(o1).length === Object.keys(o2).length &&
            Object.keys(o1).every(p => this.objectsEqual(o1[p], o2[p]))
            : (o1 !== null && Array.isArray(o1) && Array.isArray(o2) && !o1.length &&
            !o2.length) ? true : o1 === o2;
    };
}

